<template>
  <v-card class="rounded-lg channel-provider-form">
    <v-card-title class="d-flex justify-space-between align-center pa-6 pb-4">
      <span class="text-h6 font-weight-bold">{{ title }}</span>
      <v-btn icon @click="cancel" :disabled="loading">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-6 pt-0">
      <v-form ref="form" v-model="isFormValid" lazy-validation>
        <v-card outlined class="rounded-lg mb-5 section-card">
          <div class="section-header pa-3 px-4">
            <span class="white--text font-weight-bold">Data Provider</span>
          </div>
          <div class="pa-5">
            <div class="field-label">
              Nama Provider <span class="red--text">*</span>
            </div>
            <v-text-field
              v-model="formData.provider_name"
              placeholder="Masukkan nama provider"
              outlined
              dense
              :maxlength="50"
              :counter="50"
              :rules="providerNameRules"
              :error-messages="getFieldError('provider_name')"
              :disabled="loading"
              @keypress="onAlphanumericKeypress"
              @paste="onAlphanumericPaste($event, 'provider_name')"
              @input="sanitizeAlphanumeric('provider_name')"
            />

            <div class="field-label mt-2">
              URL Callback <span class="red--text">*</span>
            </div>
            <v-text-field
              v-model="formData.url_callback"
              placeholder="https://example.com/callback"
              outlined
              dense
              :rules="urlRules"
              :error-messages="getFieldError('url_callback')"
              :disabled="loading"
              @input="clearFieldError('url_callback')"
            />
          </div>
        </v-card>

        <v-card outlined class="rounded-lg section-card">
          <div class="section-header pa-3 px-4">
            <span class="white--text font-weight-bold">Data PIC</span>
          </div>
          <div class="pa-5">
            <v-row dense>
              <v-col cols="12" md="6">
                <div class="field-label">
                  Nama PIC <span class="red--text">*</span>
                </div>
                <v-text-field
                  v-model="formData.pic_name"
                  placeholder="Masukkan nama PIC"
                  outlined
                  dense
                  :maxlength="50"
                  :counter="50"
                  :rules="picNameRules"
                  :error-messages="getFieldError('pic_name')"
                  :disabled="loading"
                  @keypress="onAlphanumericKeypress"
                  @paste="onAlphanumericPaste($event, 'pic_name')"
                  @input="sanitizeAlphanumeric('pic_name')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <div class="field-label">
                  Jabatan <span class="red--text">*</span>
                </div>
                <v-text-field
                  v-model="formData.pic_job_position"
                  placeholder="Masukkan jabatan"
                  outlined
                  dense
                  :maxlength="50"
                  :counter="50"
                  :rules="jobPositionRules"
                  :error-messages="getFieldError('pic_job_position')"
                  :disabled="loading"
                  @keypress="onAlphanumericKeypress"
                  @paste="onAlphanumericPaste($event, 'pic_job_position')"
                  @input="sanitizeAlphanumeric('pic_job_position')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <div class="field-label">
                  No HP <span class="red--text">*</span>
                </div>
                <v-text-field
                  v-model="formData.pic_phone_number"
                  placeholder="08xxxxxxxxxx"
                  outlined
                  dense
                  :maxlength="13"
                  :counter="13"
                  :rules="phoneRules"
                  :error-messages="getFieldError('pic_phone_number')"
                  :disabled="loading"
                  @keypress="onNumericKeypress"
                  @paste="onNumericPaste($event, 'pic_phone_number')"
                  @input="sanitizeNumeric('pic_phone_number')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <div class="field-label">
                  Email <span class="red--text">*</span>
                </div>
                <v-text-field
                  v-model="formData.pic_email"
                  placeholder="pic@example.com"
                  outlined
                  dense
                  hint="Credential API akan dikirimkan ke email ini"
                  persistent-hint
                  :rules="emailRules"
                  :error-messages="getFieldError('pic_email')"
                  :disabled="loading"
                  @input="clearFieldError('pic_email')"
                >
                  <template v-slot:message="{ message }">
                    <template v-if="message === EMAIL_HINT">
                      <div class="d-inline-flex align-center">
                        <v-icon size="14" color="blue darken-2" class="mr-1">
                          mdi-information-outline
                        </v-icon>
                        <span class="blue--text text--darken-2">{{ message }}</span>
                      </div>
                    </template>
                    <template v-else>
                      {{ message }}
                    </template>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </div>
        </v-card>
      </v-form>
    </v-card-text>

    <v-card-actions class="pa-6 pt-2">
      <v-spacer />
      <v-btn
        outlined
        color="primary"
        class="rounded-lg px-6 mr-2"
        @click="cancel"
        :disabled="loading"
      >
        Batal
      </v-btn>
      <v-btn
        color="primary"
        class="rounded-lg elevation-0 px-6"
        @click="save"
        :loading="loading"
        :disabled="loading"
      >
        Simpan
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: 'ChannelProviderForm',
  props: {
    title: { type: String, default: 'Tambah Channel Provider' },
    editedItem: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    errors: { type: Object, default: () => ({}) },
    open: { type: Boolean, default: false },
  },
  data() {
    return {
      isFormValid: false,
      EMAIL_HINT: 'Credential API akan dikirimkan ke email ini',
      formData: {
        provider_name: '',
        pic_name: '',
        pic_job_position: '',
        pic_phone_number: '',
        pic_email: '',
        url_callback: '',
      },
      fieldErrors: {},
    };
  },
  computed: {
    providerNameRules() {
      return [
        (v) => !!v || 'Nama Provider wajib diisi',
        (v) => (v && v.length <= 50) || 'Nama Provider maksimal 50 karakter',
        (v) => /^[a-zA-Z0-9 ]+$/.test(v || '') || 'Nama Provider hanya boleh huruf dan angka',
      ];
    },
    picNameRules() {
      return [
        (v) => !!v || 'Nama PIC wajib diisi',
        (v) => (v && v.length <= 50) || 'Nama PIC maksimal 50 karakter',
        (v) => /^[a-zA-Z0-9 ]+$/.test(v || '') || 'Nama PIC hanya boleh huruf dan angka',
      ];
    },
    jobPositionRules() {
      return [
        (v) => !!v || 'Jabatan wajib diisi',
        (v) => (v && v.length <= 50) || 'Jabatan maksimal 50 karakter',
        (v) => /^[a-zA-Z0-9 ]+$/.test(v || '') || 'Jabatan hanya boleh huruf dan angka',
      ];
    },
    phoneRules() {
      return [
        (v) => !!v || 'No HP wajib diisi',
        (v) => /^\d+$/.test(v || '') || 'No HP hanya boleh angka',
        (v) => (v && v.length >= 10) || 'No HP minimal 10 digit',
        (v) => (v && v.length <= 13) || 'No HP maksimal 13 digit',
      ];
    },
    emailRules() {
      return [
        (v) => !!v || 'Email wajib diisi',
        (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '') || 'Format email tidak valid',
      ];
    },
    urlRules() {
      return [
        (v) => !!v || 'URL Callback wajib diisi',
        (v) => this.isValidUrl(v) || 'Format URL tidak valid (contoh: https://example.com/callback)',
      ];
    },
  },
  watch: {
    open: {
      handler(val) {
        if (val) {
          this.formData = {
            provider_name: this.editedItem.provider_name || '',
            pic_name: this.editedItem.pic_name || '',
            pic_job_position: this.editedItem.pic_job_position || '',
            pic_phone_number: this.editedItem.pic_phone_number || '',
            pic_email: this.editedItem.pic_email || '',
            url_callback: this.editedItem.url_callback || '',
          };
          this.fieldErrors = {};
          this.$nextTick(() => {
            if (this.$refs.form) this.$refs.form.resetValidation();
          });
        }
      },
      immediate: true,
    },
    errors: {
      handler(val) {
        if (val && typeof val === 'object') {
          this.fieldErrors = { ...val };
        }
      },
      deep: true,
    },
  },
  methods: {
    isValidUrl(v) {
      if (!v) return false;
      try {
        const u = new URL(v);
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch (e) {
        return false;
      }
    },
    onAlphanumericKeypress(event) {
      const char = String.fromCharCode(event.which || event.keyCode);
      if (!/[a-zA-Z0-9 ]/.test(char)) {
        event.preventDefault();
      }
    },
    onNumericKeypress(event) {
      const char = String.fromCharCode(event.which || event.keyCode);
      if (!/[0-9]/.test(char)) {
        event.preventDefault();
      }
    },
    onAlphanumericPaste(event, key) {
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData).getData('text') || '';
      const sanitized = text.replace(/[^a-zA-Z0-9 ]/g, '');
      const current = this.formData[key] || '';
      const maxLen = 50;
      this.formData[key] = (current + sanitized).slice(0, maxLen);
      this.clearFieldError(key);
    },
    onNumericPaste(event, key) {
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData).getData('text') || '';
      const sanitized = text.replace(/[^0-9]/g, '');
      const current = this.formData[key] || '';
      const maxLen = 13;
      this.formData[key] = (current + sanitized).slice(0, maxLen);
      this.clearFieldError(key);
    },
    sanitizeAlphanumeric(key) {
      const value = this.formData[key] || '';
      const sanitized = value.replace(/[^a-zA-Z0-9 ]/g, '');
      if (sanitized !== value) {
        this.formData[key] = sanitized;
      }
      this.clearFieldError(key);
    },
    sanitizeNumeric(key) {
      const value = this.formData[key] || '';
      const sanitized = value.replace(/[^0-9]/g, '');
      if (sanitized !== value) {
        this.formData[key] = sanitized;
      }
      this.clearFieldError(key);
    },
    getFieldError(key) {
      const err = this.fieldErrors[key];
      if (!err) return [];
      if (Array.isArray(err)) return err;
      return [err];
    },
    clearFieldError(key) {
      if (this.fieldErrors[key]) {
        const next = { ...this.fieldErrors };
        delete next[key];
        this.fieldErrors = next;
      }
    },
    cancel() {
      this.$emit('cancel');
    },
    save() {
      if (this.$refs.form.validate()) {
        const trimmed = {
          provider_name: (this.formData.provider_name || '').trim(),
          pic_name: (this.formData.pic_name || '').trim(),
          pic_job_position: (this.formData.pic_job_position || '').trim(),
          pic_phone_number: (this.formData.pic_phone_number || '').trim(),
          pic_email: (this.formData.pic_email || '').trim(),
          url_callback: (this.formData.url_callback || '').trim(),
        };
        this.$emit('save', trimmed);
      }
    },
  },
};
</script>

<style scoped>
.section-card {
  overflow: hidden;
}
.section-header {
  background-color: #378595;
}
.field-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
  color: #333;
}
::v-deep .v-text-field--outlined.v-input--dense .v-input__control > .v-input__slot {
  min-height: 44px;
}
</style>
